import {
    ADMIN,
    SUB_ADMIN,
    TEAM_LEAD,
    YT_HELPER_LEAD,
    QA_USER,
    YT_HELPER,
    CUSTOMER,
} from "../api/Constants";
import { AiOutlineCloudUpload, AiOutlineUserSwitch, AiOutlineYoutube, AiOutlineDashboard, AiOutlineRobot } from "react-icons/ai";
import { HiOutlineUserGroup } from "react-icons/hi";
import { RiArchiveDrawerLine } from "react-icons/ri";
import { BiHomeAlt, BiStar, BiCard, BiMessageAlt, BiExport, BiListPlus } from "react-icons/bi";
import { FiRepeat, FiSettings } from "react-icons/fi";

/**
 * Single source of truth for the white-label sidebar links.
 *
 * - `key`   must match the MenuItem key used in components/Header.js
 * - `label` is what the admin sees in the "Sidebar Access" modal
 *
 * The per-portal setting is stored in website_type.sidebar_links_access as JSON:
 *   { "<key>": { "all": bool, "admin": bool, "customer": bool, "qa": bool, "teamlead": bool }, ... }
 */
export const SIDEBAR_LINKS = [
    { key: "home",             label: "Home",                 path: "/",                   icon: BiHomeAlt },
    { key: "upload",           label: "Upload",               path: "/upload",             icon: AiOutlineCloudUpload },
    { key: "users",            label: "Users / My Team",      path: "/users",              icon: AiOutlineUserSwitch },
    { key: "customers",        label: "Customers",            path: "/customers",          icon: HiOutlineUserGroup },
    { key: "ytHelper",         label: "YouTube Helper",       path: "/ythelper/dashboard", icon: AiOutlineYoutube },
    { key: "dueCards",         label: "Due Cards",            path: "/dueCards",           icon: BiCard },
    { key: "exportCards",      label: "Export Cards",         path: "/exportCards",        icon: BiExport },
    { key: "videosList",       label: "Videos List",          path: "/videosList",         icon: BiListPlus },
    { key: "billingCycle",     label: "Portal Billing Cycle", path: "/billingCycle",       icon: FiRepeat },
    { key: "portalManagement", label: "Portal Management",    path: "/portalManagement",   icon: FiSettings },
    { key: "inbox",            label: "Support (Inbox)",      path: "/inbox",              icon: BiMessageAlt },
    { key: "dashboard",        label: "Dashboard",            path: "/dashboard",          icon: AiOutlineDashboard },
    { key: "archived",         label: "Archived",             path: "/cards/archive",      icon: RiArchiveDrawerLine },
    { key: "rating",           label: "Rating",               path: "/rating",             icon: BiStar },
    { key: "AIToolbox",        label: "AI Toolbox",           path: "/AIToolbox",          icon: AiOutlineRobot },
];

/**
 * Role groups the admin can toggle per link (column order in the modal).
 * `members` lists the client_type values that fall into each group.
 */
export const SIDEBAR_ACCESS_ROLES = [
    { key: "admin",    label: "Admin",     short: "AD", members: [ADMIN, SUB_ADMIN] },
    { key: "customer", label: "Customer",  short: "CU", members: [CUSTOMER] },
    { key: "qa",       label: "QA",        short: "QA", members: [QA_USER, YT_HELPER] },
    { key: "teamlead", label: "Team Lead", short: "TL", members: [TEAM_LEAD, YT_HELPER_LEAD] },
];

export const SIDEBAR_ACCESS_ALL = "all";

/**
 * Map a user's client_type to the role group used in sidebar_links_access.
 * Grouping mirrors userTypeOptions() in Header.js.
 */
export function getSidebarRoleGroup(clientType) {
    switch (clientType) {
        case ADMIN:
        case SUB_ADMIN:
            return "admin";
        case TEAM_LEAD:
        case YT_HELPER_LEAD:
            return "teamlead";
        case QA_USER:
        case YT_HELPER:
            return "qa";
        case CUSTOMER:
            return "customer";
        default:
            return null;
    }
}

/** Every link visible to every role. Used when a portal has never been configured. */
export function buildDefaultSidebarAccess() {
    const access = {};
    SIDEBAR_LINKS.forEach(({ key }) => {
        access[key] = { [SIDEBAR_ACCESS_ALL]: true };
        SIDEBAR_ACCESS_ROLES.forEach(({ key: role }) => {
            access[key][role] = true;
        });
    });
    return access;
}

/**
 * Accepts whatever came back from the API (object, JSON string, or null)
 * and returns a full access map with every SIDEBAR_LINKS key present.
 * Links missing from the stored JSON default to visible for all roles.
 */
export function normalizeSidebarAccess(raw) {
    let stored = raw;
    if (typeof stored === "string") {
        try {
            stored = JSON.parse(stored);
        } catch (e) {
            stored = null;
        }
    }
    const defaults = buildDefaultSidebarAccess();
    if (!stored || typeof stored !== "object") {
        return defaults;
    }
    const merged = {};
    SIDEBAR_LINKS.forEach(({ key }) => {
        const entry = stored[key];
        if (!entry || typeof entry !== "object") {
            merged[key] = defaults[key];
            return;
        }
        const roles = {};
        SIDEBAR_ACCESS_ROLES.forEach(({ key: role }) => {
            roles[role] = Boolean(entry[SIDEBAR_ACCESS_ALL]) || Boolean(entry[role]);
        });
        merged[key] = {
            [SIDEBAR_ACCESS_ALL]: SIDEBAR_ACCESS_ROLES.every(({ key: role }) => roles[role]),
            ...roles,
        };
    });
    return merged;
}

/**
 * Path of the first sidebar link the user's role is allowed to open, or null when none.
 * Used to redirect users who type a restricted URL directly.
 */
export function firstAllowedSidebarPath(rawAccess, clientType) {
    const link = SIDEBAR_LINKS.find(({ key }) => isSidebarLinkAllowed(rawAccess, key, clientType));
    return link ? link.path : null;
}

/**
 * Decide whether a sidebar link should be rendered for the logged in user.
 *
 * @param {object|string|null} rawAccess  subDomainData.sidebar_links_access
 * @param {string} linkKey                 key from SIDEBAR_LINKS
 * @param {string} clientType              loggedInUser.client_type
 * @returns {boolean}
 */
export function isSidebarLinkAllowed(rawAccess, linkKey, clientType) {
    // Portal never configured -> legacy behaviour, show everything.
    if (rawAccess === null || rawAccess === undefined || rawAccess === "") {
        return true;
    }
    const roleGroup = getSidebarRoleGroup(clientType);
    if (!roleGroup) {
        return true;
    }
    const access = normalizeSidebarAccess(rawAccess);
    const entry = access[linkKey];
    // Unknown link key (not in SIDEBAR_LINKS) is never hidden by this setting.
    if (!entry) {
        return true;
    }
    return Boolean(entry[SIDEBAR_ACCESS_ALL]) || Boolean(entry[roleGroup]);
}
